import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator, X, Minus, Copy, Check, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export function FloatingCalculator() {
  const [isOpen, setIsOpen] = useState(false);
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [memory, setMemory] = useState<number>(0);
  const [hasMemory, setHasMemory] = useState(false);
  
  const isMobile = useIsMobile();
  const calculatorRef = useRef<HTMLDivElement>(null);

  // Input digit handler
  const inputDigit = useCallback((digit: string) => {
    setDisplay(prev => {
      if (waitingForOperand) {
        setWaitingForOperand(false);
        return digit;
      }
      return prev === "0" ? digit : prev + digit;
    });
  }, [waitingForOperand]);

  // Input decimal handler
  const inputDecimal = useCallback(() => {
    setDisplay(prev => {
      if (waitingForOperand) {
        setWaitingForOperand(false);
        return "0.";
      }
      return prev.includes(".") ? prev : prev + ".";
    });
  }, [waitingForOperand]);

  // Clear handler
  const clear = useCallback(() => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  }, []);

  // Backspace handler
  const backspace = useCallback(() => {
    setDisplay(prev => {
      if (prev.length === 1 || (prev.length === 2 && prev.startsWith("-"))) {
        return "0";
      }
      return prev.slice(0, -1);
    });
  }, []);

  // Perform operation handler
  const performOperation = useCallback((nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue;
      let newValue: number;

      switch (operation) {
        case "+":
          newValue = currentValue + inputValue;
          break;
        case "-":
          newValue = currentValue - inputValue;
          break;
        case "×":
        case "*":
          newValue = currentValue * inputValue;
          break;
        case "÷":
        case "/":
          newValue = inputValue !== 0 ? currentValue / inputValue : 0;
          break;
        case "%":
          newValue = (currentValue * inputValue) / 100;
          break;
        default:
          newValue = inputValue;
      }

      // Round to avoid floating point errors
      newValue = Math.round(newValue * 1000000) / 1000000;
      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation === "*" ? "×" : nextOperation === "/" ? "÷" : nextOperation);
  }, [display, previousValue, operation]);

  // Calculate result handler
  const calculate = useCallback(() => {
    if (operation && previousValue !== null) {
      performOperation("=");
      setOperation(null);
      setPreviousValue(null);
      setWaitingForOperand(true);
    }
  }, [operation, previousValue, performOperation]);

  // Toggle sign handler
  const toggleSign = useCallback(() => {
    setDisplay(prev => String(-parseFloat(prev)));
  }, []);

  // Memory functions
  const memoryAdd = useCallback(() => {
    setMemory(prev => prev + parseFloat(display));
    setHasMemory(true);
    toast.success("Added to memory");
  }, [display]);

  const memorySubtract = useCallback(() => {
    setMemory(prev => prev - parseFloat(display));
    setHasMemory(true);
    toast.success("Subtracted from memory");
  }, [display]);

  const memoryRecall = useCallback(() => {
    if (hasMemory) {
      setDisplay(String(memory));
      setWaitingForOperand(true);
    }
  }, [memory, hasMemory]);

  const memoryClear = useCallback(() => {
    setMemory(0);
    setHasMemory(false);
    toast.success("Memory cleared");
  }, []);

  // Copy to clipboard handler
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(display);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  }, [display]);

  // Global keyboard shortcut (Alt+G) to toggle calculator
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setIsMinimized(false);
      }
      // Also support Ctrl+= for toggle
      if ((e.ctrlKey || e.metaKey) && e.key === '=') {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setIsMinimized(false);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Calculator-specific keyboard handling when open
  useEffect(() => {
    if (!isOpen || isMinimized) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere with form inputs unless calculator is focused
      const target = e.target as HTMLElement;
      const isInCalculator = calculatorRef.current?.contains(target);
      const isFormInput = target instanceof HTMLInputElement || 
                          target instanceof HTMLTextAreaElement ||
                          target.isContentEditable;
      
      // Only handle keys if we're in the calculator or not in a form input
      if (isFormInput && !isInCalculator) return;

      // Handle Escape to clear calculation (not close)
      if (e.key === 'Escape') {
        e.preventDefault();
        clear();
        return;
      }

      // Handle number keys
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        inputDigit(e.key);
        return;
      }

      // Handle operators
      switch (e.key) {
        case '+':
          e.preventDefault();
          performOperation('+');
          break;
        case '-':
          e.preventDefault();
          performOperation('-');
          break;
        case '*':
          e.preventDefault();
          performOperation('×');
          break;
        case '/':
          e.preventDefault();
          performOperation('÷');
          break;
        case '%':
          e.preventDefault();
          performOperation('%');
          break;
        case '.':
        case ',':
          e.preventDefault();
          inputDecimal();
          break;
        case 'Enter':
          e.preventDefault();
          calculate();
          break;
        case 'Backspace':
          e.preventDefault();
          backspace();
          break;
        case 'Delete':
        case 'c':
        case 'C':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            clear();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isMinimized, inputDigit, inputDecimal, performOperation, calculate, backspace, clear]);

  // Focus calculator when opened
  useEffect(() => {
    if (isOpen && !isMinimized && calculatorRef.current) {
      calculatorRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const formatDisplay = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    if (value.endsWith(".")) return value;
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const handleButtonClick = useCallback((btn: string) => {
    switch (btn) {
      case "C":
        clear();
        break;
      case "±":
        toggleSign();
        break;
      case ".":
        inputDecimal();
        break;
      case "=":
        calculate();
        break;
      case "+":
      case "-":
      case "×":
      case "÷":
      case "%":
        performOperation(btn);
        break;
      case "⌫":
        backspace();
        break;
      case "M+":
        memoryAdd();
        break;
      case "M-":
        memorySubtract();
        break;
      case "MR":
        memoryRecall();
        break;
      case "MC":
        memoryClear();
        break;
      default:
        if (/^[0-9]$/.test(btn)) {
          inputDigit(btn);
        }
    }
  }, [clear, toggleSign, inputDecimal, calculate, performOperation, backspace, inputDigit, memoryAdd, memorySubtract, memoryRecall, memoryClear]);

  // Floating button when closed
  if (!isOpen) {
    return (
      <Button
        onClick={() => {
          setIsOpen(true);
          setIsMinimized(false);
        }}
        className="fixed bottom-20 right-4 z-50 h-14 w-14 rounded-full shadow-lg transition-transform hover:scale-105 md:bottom-6"
        size="icon"
        title="Calculator (Alt+G)"
      >
        <Calculator className="h-6 w-6" />
      </Button>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div className="fixed bottom-20 right-4 z-50 md:bottom-6">
        <Button
          onClick={() => setIsMinimized(false)}
          className="h-14 w-14 rounded-full shadow-lg transition-transform hover:scale-105"
          size="icon"
          title="Restore Calculator"
        >
          <Calculator className="h-6 w-6" />
        </Button>
        {display !== "0" && (
          <div className="absolute -top-2 -left-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full shadow">
            ₹{formatDisplay(display).slice(0, 8)}
          </div>
        )}
      </div>
    );
  }

  const mainButtons = [
    ["C", "±", "%", "÷"],
    ["7", "8", "9", "×"],
    ["4", "5", "6", "-"],
    ["1", "2", "3", "+"],
    ["0", ".", "⌫", "="],
  ];

  const memoryButtons = ["MC", "MR", "M-", "M+"];

  // Mobile bottom sheet style
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-200"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Bottom Sheet Calculator */}
        <div 
          ref={calculatorRef}
          tabIndex={-1}
          className="fixed bottom-0 left-0 right-0 z-50 bg-background rounded-t-3xl shadow-2xl border-t-2 border-border animate-in slide-in-from-bottom duration-300 outline-none"
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-12 h-1 bg-muted-foreground/30 rounded-full" />
          </div>
          
          {/* Header */}
          <div className="flex items-center justify-between px-4 pb-2">
            <div className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-primary" />
              <span className="font-semibold">Calculator</span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Keyboard className="h-3 w-3" />
                <span>Keyboard enabled</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="px-4 pb-6">
            {/* Display */}
            <div className="mb-4 rounded-xl bg-muted p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-muted-foreground">
                  {previousValue !== null && operation ? `${formatDisplay(String(previousValue))} ${operation}` : ""}
                </span>
                <div className="flex items-center gap-2">
                  {hasMemory && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">M</span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={copyToClipboard}
                  >
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="text-right text-4xl font-bold text-foreground truncate">
                ₹{formatDisplay(display)}
              </div>
            </div>

            {/* Memory Buttons */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              {memoryButtons.map((btn) => (
                <Button
                  key={btn}
                  variant="outline"
                  className={cn(
                    "h-12 text-sm font-semibold",
                    btn === "MR" && hasMemory && "border-primary text-primary"
                  )}
                  onClick={() => handleButtonClick(btn)}
                  disabled={btn === "MR" && !hasMemory}
                >
                  {btn}
                </Button>
              ))}
            </div>

            {/* Main Buttons */}
            <div className="grid gap-2">
              {mainButtons.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-4 gap-2">
                  {row.map((btn) => {
                    const isOperator = ["+", "-", "×", "÷", "%"].includes(btn);
                    const isEquals = btn === "=";
                    const isClear = btn === "C";
                    const isBackspace = btn === "⌫";

                    return (
                      <Button
                        key={btn}
                        variant={isEquals ? "default" : isOperator || isClear ? "secondary" : "outline"}
                        className={cn(
                          "h-16 text-xl font-semibold transition-transform active:scale-95",
                          isEquals && "bg-primary hover:bg-primary/90"
                        )}
                        onClick={() => handleButtonClick(btn)}
                      >
                        {btn}
                      </Button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Quick amounts */}
            <div className="mt-4 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Quick Add</p>
              <div className="grid grid-cols-4 gap-2">
                {[100, 500, 1000, 5000].map((amount) => (
                  <Button
                    key={amount}
                    variant="ghost"
                    className="text-sm h-10"
                    onClick={() => {
                      const current = parseFloat(display) || 0;
                      setDisplay(String(current + amount));
                    }}
                  >
                    +{amount}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop calculator
  return (
    <Card 
      ref={calculatorRef}
      tabIndex={-1}
      className="fixed bottom-20 right-4 z-50 w-[340px] shadow-2xl border-2 md:bottom-6 animate-in slide-in-from-bottom-5 duration-200 outline-none"
    >
      <CardHeader className="pb-2 pt-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          Calculator
          <span className="text-xs text-muted-foreground font-normal flex items-center gap-1">
            <Keyboard className="h-3 w-3" />
            Alt+G
          </span>
        </CardTitle>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsMinimized(true)}
            title="Minimize"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsOpen(false)}
            title="Close (Esc)"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {/* Display */}
        <div className="mb-3 rounded-xl bg-muted p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">
              {previousValue !== null && operation ? `${formatDisplay(String(previousValue))} ${operation}` : ""}
            </span>
            <div className="flex items-center gap-2">
              {hasMemory && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-medium">
                  M: ₹{formatDisplay(String(memory))}
                </span>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={copyToClipboard}
                title="Copy to clipboard"
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="text-right text-3xl font-bold text-foreground truncate">
            ₹{formatDisplay(display)}
          </div>
        </div>

        {/* Memory Buttons */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {memoryButtons.map((btn) => (
            <Button
              key={btn}
              variant="outline"
              className={cn(
                "h-10 text-xs font-semibold",
                btn === "MR" && hasMemory && "border-primary text-primary"
              )}
              onClick={() => handleButtonClick(btn)}
              disabled={btn === "MR" && !hasMemory}
              title={
                btn === "MC" ? "Memory Clear" :
                btn === "MR" ? "Memory Recall" :
                btn === "M-" ? "Memory Subtract" :
                "Memory Add"
              }
            >
              {btn}
            </Button>
          ))}
        </div>

        {/* Main Buttons */}
        <div className="grid gap-2">
          {mainButtons.map((row, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-4 gap-2">
              {row.map((btn) => {
                const isOperator = ["+", "-", "×", "÷", "%"].includes(btn);
                const isEquals = btn === "=";
                const isClear = btn === "C";

                return (
                  <Button
                    key={btn}
                    variant={isEquals ? "default" : isOperator || isClear ? "secondary" : "outline"}
                    className={cn(
                      "h-12 text-lg font-semibold transition-transform active:scale-95",
                      isEquals && "bg-primary hover:bg-primary/90"
                    )}
                    onClick={() => handleButtonClick(btn)}
                  >
                    {btn}
                  </Button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Quick amounts */}
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Quick Add</p>
          <div className="grid grid-cols-4 gap-2">
            {[100, 500, 1000, 5000].map((amount) => (
              <Button
                key={amount}
                variant="ghost"
                size="sm"
                className="text-xs h-9"
                onClick={() => {
                  const current = parseFloat(display) || 0;
                  setDisplay(String(current + amount));
                }}
              >
                +{amount}
              </Button>
            ))}
          </div>
        </div>

        {/* Keyboard hints */}
        <div className="mt-3 pt-2 border-t border-border text-xs text-muted-foreground">
          <span className="font-medium">Keyboard:</span> 0-9, +, -, *, /, Enter, Backspace, Esc
        </div>
      </CardContent>
    </Card>
  );
}